import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';

import { chatAPI } from 'services/api';
import { RootState } from 'store/rootReducer';

import {
  CanalInfoInterfaceV2,
  CanalInterface,
  TipoCanalEnum,
  TipoCanalTypes,
} from './canais';
import { EquipeInterface, EquipeInterfaceV2 } from './equipes';
import { TagInterface, TagInterfaceV2 } from './tags';
import { UsuarioInterface, UsuarioInterfaceV2 } from './usuarios';

export type ContatoStatusTypes = 'VALIDO' | 'INVALIDO' | 'NAO_VERIFICADO';
export type ContatoTypes = 'PRIVADO' | 'ANONIMO';

export enum MidiasContatoEnum {
  IMAGEM = 'IMAGEM',
  VIDEO = 'VIDEO',
  AUDIO = 'AUDIO',
  DOCUMENTO = 'DOCUMENTO',
}

export enum CustomFieldsEnum {
  CNPJ = 'CNPJ',
  CPF = 'CPF',
  EMAIL = 'EMAIL',
  LOGICAL = 'LOGICAL',
  MONEY = 'MONEY',
  INTEGER = 'INTEGER',
  SHORT_TEXT = 'SHORT_TEXT',
  LONG_TEXT = 'LONG_TEXT',
  URL = 'URL',
  DATE = 'DATE',
}

export declare interface CustomFieldInterface {
  id: string;
  type: CustomFieldsEnum;
  name: string;
  description?: string;
  value?: string;
}

export declare interface CreateCustomFieldInterface {
  id: string;
  value: string | number | boolean | undefined | null;
}

export declare interface ContatoInterface {
  id: string;
  nome: string;
  contato: string;
  foto: string;
  status: ContatoStatusTypes;
  tipoCanal: TipoCanalTypes;
  tipo: ContatoTypes;
  tags: TagInterface[];
  equipePadrao: EquipeInterface | null;
  usuarioPadrao: UsuarioInterface | null;
  canal: CanalInterface | null;
  observacao?: string | null;
  blockedAt?: string | null;
  bloqueadoEm?: string | null;
  waPhoneNumberId?: string | null;
}

export declare interface ContatoInterfaceV2 {
  id: string;
  name: string;
  contact: string;
  fotoUrl: string;
  observation: string | null;
  status: ContatoStatusTypes;
  tags: TagInterfaceV2[];
  type: ContatoTypes;
  tipoCanal: TipoCanalTypes;
  teamDefault: EquipeInterfaceV2 | null;
  atendenteDefault: UsuarioInterfaceV2 | null;
  customFields: CustomFieldInterface[];
  channel: CanalInfoInterfaceV2 | null;
  blockedAt?: string | null;
  bloqueadoEm?: string | null;
}

export declare interface ContatoSelectInterface {
  id: string;
  name: string;
  contact: string;
  type: ContatoTypes;
  status: ContatoStatusTypes;
  fotoUrl: string;
  channelType: TipoCanalTypes;
  channel: Pick<CanalInfoInterfaceV2, 'id' | 'name' | 'type'> | null;
  blockedAt?: string | null;
}

export declare interface MidiasContatoInterface {
  id: string;
  order: number;
  filename: string;
  mimetype: string;
  size: number;
  url: string;
  title: string;
  registeredIn: string;
  author: string;
  type: MidiasContatoEnum;
}

export type GetContactFunnelsAndOpportunitiesResponse =
  ContactFunnelsAndOpportunitiesItem[];

export interface ContactFunnelsAndOpportunitiesItem {
  funnel: ContactFunnelWithOpportunities;
}

export interface ContactFunnelWithOpportunities {
  id: string;
  name: string;
  code: string;
  opportunities: ContactFunnelOpportunity[];
}

export interface ContactFunnelOpportunity {
  id: string;
  code: string;
  opportunityValue: number;
  description: string;
  createdAt: string;
  stageStatus: string | null;
  sourceName: string | null;
  tags: {
    id: string;
    color: string;
    name: string;
  }[];
  stageId: string;
  stageName: string;
}

type ContatosState = {
  contatos: ContatoInterface[];
  contatosCursor: CursorInterface;
  loadingContatos: boolean;

  contatosV2: ContatoInterfaceV2[];
  contatosV2InfoPage: { page: number; total: number };
  loadingContatosV2: boolean;

  contatoAtendimento: ContatoInterface | null;
  loadingContatoAtendimento: boolean;

  contatoAtendimentoV2: ContatoInterfaceV2 | null;
  loadingContatoAtendimentoV2: boolean;

  midiasContato: MidiasContatoInterface[];
  midiasContatoCursor: CursorInterface;
  loadingMidiasContato: boolean;

  contatosHistorico: ContatoInterface[];
  contatosHistoricoCursor: CursorInterface;
  loadingContatosHistorico: boolean;

  contatosDialog: ContatoInterface[];
  contatosDialogCursor: CursorInterface;
  loadingContatosDialog: boolean;

  contatosDialogV2: ContatoSelectInterface[];
  contatosDialogV2Cursor: CursorInterface;
  loadingContatosDialogV2: boolean;

  loadingRemoverContato: boolean;
  loadingAddTagContato: boolean;
  loadingRemoveTagContato: boolean;

  loadingdefinirEquipeOuAtendentePadrao: boolean;

  customFields: CustomFieldInterface[];
  loadingCustomFields: boolean;

  funnelsAndOpportunities: GetContactFunnelsAndOpportunitiesResponse;
  loadingFunnelsAndOpportunities: boolean;
};

// async thunks

export const fetchContatos = createAsyncThunk(
  'contatos/fetchContatos',
  async (params?: {
    filter?: string;
    filters?: string;
    cursor?: CursorInterface;
  }) => {
    const response = await chatAPI.get('contatos-next', {
      params: {
        filter: params?.filter,
        filters: params?.filters,
        ...params?.cursor,
      },
    });
    return {
      data: response.data.data as ContatoInterface[],
      cursor: response.data.cursor as CursorInterface,
    };
  },
);

export const fetchContatosV2 = createAsyncThunk(
  'contatos/fetchContatosV2',
  async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    tagIds?: string[];
    blockStatus?: string;
  }) => {
    const response = await chatAPI.get('v2/contacts', {
      params: {
        page: params?.page ?? 1,
        limit: params?.limit ?? 25,
        name: params?.search || undefined,
        tagIds: params?.tagIds,
        blockStatus: params?.blockStatus,
      },
    });
    return {
      data: response.data.data as ContatoInterfaceV2[],
      total: response.data.total as number,
      page: params?.page ?? 1,
    };
  },
);

export const fetchContatosHistorico = createAsyncThunk(
  'contatos/fetchContatosHistorico',
  async (params?: { filter?: string; cursor?: CursorInterface }) => {
    const response = await chatAPI.get('contatos-next', {
      params: { filter: params?.filter, ...params?.cursor },
    });
    return {
      data: response.data.data as ContatoInterface[],
      cursor: response.data.cursor as CursorInterface,
    };
  },
);

export const fetchContatosDialog = createAsyncThunk(
  'contatos/fetchContatosDialog',
  async (params?: { filter?: string; cursor?: CursorInterface }) => {
    const response = await chatAPI.get('contatos-next', {
      params: { filter: params?.filter, ...params?.cursor },
    });
    return {
      data: response.data.data as ContatoInterface[],
      cursor: response.data.cursor as CursorInterface,
    };
  },
);

export const fetchContatosDialogV2 = createAsyncThunk(
  'v2/contacts/select',
  async (params?: {
    search?: string;
    channelType?: TipoCanalEnum;
    contact?: string;
    cursor?: CursorInterface;
  }) => {
    const response = await chatAPI.get('v2/contacts/select', {
      params: {
        search: params?.search ? params?.search : undefined,
        channelType: params?.channelType,
        contact: params?.contact,
        limit: 50,
        ...params?.cursor,
      },
    });
    return {
      data: response.data.data as ContatoSelectInterface[],
      cursor: response.data.cursor as CursorInterface,
    };
  },
);

export const fetchMidiasContato = createAsyncThunk(
  'v2/contacts/fetchMidiasContato',
  async (data: {
    contatoId: string;
    channelCode: string;
    type: MidiasContatoEnum;
    cursor?: CursorInterface;
  }) => {
    const { contatoId, channelCode, type, cursor } = data;
    const response = await chatAPI.get(`v2/contacts/${contatoId}/midias`, {
      params: {
        type,
        channelCode,
        limit: 50,
        ...cursor,
      },
    });
    return {
      data: response.data.data as MidiasContatoInterface[],
      cursor: response.data.cursor as CursorInterface,
    };
  },
);

export const addContato = createAsyncThunk(
  'contatos/addContato',
  async (values: { nome: string; contato: string }, { rejectWithValue }) => {
    try {
      const response = await chatAPI.post('contatos', {
        ...values,
        tipoCanal: 'WHATSAPP',
      });
      return response.data as ContatoInterface;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const addContatoV2 = createAsyncThunk(
  'v2/contatos/addContato',
  async (
    values: {
      name: string;
      phoneNumber: string;
      observation?: string;
      customFields: { id: string; value: string }[];
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await chatAPI.post('v2/contacts', {
        ...values,
        channelType: 'WHATSAPP',
      });
      return response.data as ContatoInterface;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const alterarContato = createAsyncThunk(
  'contatos/alterarContato',
  async (
    values: { contatoId: string; nome: string; observacao?: string | null },
    { rejectWithValue },
  ) => {
    try {
      const { contatoId, ...restValues } = values;
      const response = await chatAPI.put(`contatos/${contatoId}`, {
        ...restValues,
      });
      return response.data as ContatoInterface;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const alterarContatoV2 = createAsyncThunk(
  'v2/contatos/alterarContato',
  async (
    values: {
      contatoId: string;
      name: string;
      observation?: string | null;
      customFields: CreateCustomFieldInterface[];
    },
    { rejectWithValue },
  ) => {
    try {
      const { contatoId, ...restValues } = values;
      const response = await chatAPI.put(`v2/contacts/${contatoId}`, {
        ...restValues,
      });
      return response.data as ContatoInterfaceV2;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const corrigirContato = createAsyncThunk(
  'contatos/corrigirContato',
  async (
    values: { contatoId: string; contato: string },
    { rejectWithValue },
  ) => {
    try {
      const { contatoId, contato } = values;
      const response = await chatAPI.put(
        `/contatos/${contatoId}/corrigir/contato`,
        { contato },
      );
      return response.data as ContatoInterface;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const removerContato = createAsyncThunk(
  'contatos/removerContato',
  async (contatoId: string, { rejectWithValue }) => {
    try {
      const response = await chatAPI.delete(`contatos/${contatoId}`);
      return response.data as ContatoInterface;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const removerContatoV2 = createAsyncThunk(
  'v2/contatos/removerContato',
  async (contatoId: string, { rejectWithValue }) => {
    try {
      const response = await chatAPI.delete(`v2/contacts/${contatoId}`);
      return response.data as ContatoInterface;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const fetchAddTagContato = createAsyncThunk(
  'contatos/fetchAddTagContato',
  async (
    params: {
      tagId: string;
      contatoId: string;
      isContatoAtendimento?: boolean;
    },
    { rejectWithValue },
  ) => {
    try {
      const { contatoId, tagId, isContatoAtendimento } = params;
      const response = await chatAPI.post(`tags/${tagId}/contato/${contatoId}`);

      return {
        data: response.data.data as TagInterface,
        contatoId,
        isContatoAtendimento,
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const fetchRemoveTagContato = createAsyncThunk(
  'contatos/fetchRemoveTagContato',
  async (
    params: {
      tagId: string;
      contatoId: string;
      isContatoAtendimento?: boolean;
    },
    { rejectWithValue },
  ) => {
    try {
      const { tagId, contatoId, isContatoAtendimento } = params;
      const response = await chatAPI.delete(
        `tags/${tagId}/contato/${contatoId}`,
      );
      return {
        data: response.data as TagInterface,
        tagId,
        contatoId,
        isContatoAtendimento,
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const fetchContatoAtendimento = createAsyncThunk(
  'contatos/fetchContatoAtendimento',
  async (contatoId: string) => {
    const response = await chatAPI.get(`contatos-next/${contatoId}`);
    return response.data.data as ContatoInterface;
  },
);

export const fetchContatoAtendimentoV2 = createAsyncThunk(
  'contatos/fetchContatoAtendimentoV2',
  async (contatoId: string) => {
    const response = await chatAPI.get(`v2/contacts/${contatoId}`);
    return response.data as ContatoInterfaceV2;
  },
);

export const fetchCustomFields = createAsyncThunk(
  'contatos/fetchCustomFields',
  async (_, { rejectWithValue }) => {
    try {
      const response = await chatAPI.get('v2/configurations/custom-fields');

      return response.data as CustomFieldInterface[];
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const definirEquipeOuAtendentePadrao = createAsyncThunk(
  'contatos/definirEquipeOuAtendentePadrao',
  async (
    values: { contatoId: string; equipeId: string; usuarioId: string | null },
    { rejectWithValue },
  ) => {
    try {
      const { contatoId, equipeId, usuarioId } = values;
      const response = await chatAPI.put(
        `/contatos-next/${contatoId}/definir-equipe-ou-atendente-padrao`,
        { equipeId, usuarioId },
      );
      return response.data as ContatoInterface;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const removerEquipeOuAtendentePadrao = createAsyncThunk(
  'contatos/removerEquipeOuAtendentePadrao',
  async (contatoId: string, { rejectWithValue }) => {
    try {
      const response = await chatAPI.put(
        `contatos-next/${contatoId}/remover-equipe-e-atendente-padrao`,
      );
      return response.data as ContatoInterface;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const fetchFunnelsAndOpportunities = createAsyncThunk(
  'contatos/fetchFunnelsAndOpportunities',
  async (contatoId: string, { rejectWithValue }) => {
    try {
      const response = await chatAPI.get(
        `v2/contacts/${contatoId}/funnels-and-opportunities`,
      );
      return response.data as GetContactFunnelsAndOpportunitiesResponse;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

// reducer

const initialState = {
  contatos: [],
  contatosCursor: { afterCursor: '', beforeCursor: '' },
  loadingContatos: false,

  contatosV2: [],
  contatosV2InfoPage: { page: 1, total: 0 },
  loadingContatosV2: false,

  contatoAtendimento: null,
  loadingContatoAtendimento: false,

  contatoAtendimentoV2: null,
  loadingContatoAtendimentoV2: false,

  midiasContato: [],
  midiasContatoCursor: { afterCursor: '', beforeCursor: '' },
  loadingMidiasContato: false,

  contatosHistorico: [],
  contatosHistoricoCursor: { afterCursor: '', beforeCursor: '' },
  loadingContatosHistorico: false,

  contatosDialog: [],
  contatosDialogCursor: { afterCursor: '', beforeCursor: '' },
  loadingContatosDialog: false,

  contatosDialogV2: [],
  contatosDialogV2Cursor: { afterCursor: '', beforeCursor: '' },
  loadingContatosDialogV2: false,

  loadingRemoverContato: false,

  loadingAddTagContato: false,
  loadingRemoveTagContato: false,

  loadingdefinirEquipeOuAtendentePadrao: false,

  customFields: [],
  loadingCustomFields: false,

  funnelsAndOpportunities: [],
  loadingFunnelsAndOpportunities: false,
} as ContatosState;

export const contatosSlice = createSlice({
  name: 'contatos',
  initialState,
  reducers: {
    atualizarContatoV2(state, action: PayloadAction<ContatoInterfaceV2>) {
      const contato = action.payload;
      state.contatoAtendimentoV2 = contato;
    },
    updateListaContatos(
      state,
      action: PayloadAction<Partial<ContatoInterface>>,
    ) {
      const contato = action.payload;

      // Atualiza lista V1
      const index = state.contatos.findIndex(c => c.id === contato.id);
      if (index > -1) {
        state.contatos[index] = { ...state.contatos[index], ...contato };
      }

      // Atualiza lista V2 (usada pela página de Contatos)
      if (Array.isArray(state.contatosV2)) {
        const indexV2 = state.contatosV2.findIndex(c => c.id === contato.id);
        if (indexV2 > -1) {
          const c = state.contatosV2[indexV2];

          if (contato.nome !== undefined) c.name = contato.nome;
          if (contato.observacao !== undefined)
            c.observation = contato.observacao ?? null;

          if ('equipePadrao' in contato) {
            c.teamDefault = contato.equipePadrao
              ? { id: contato.equipePadrao.id, name: contato.equipePadrao.nome }
              : null;
          }

          if ('usuarioPadrao' in contato) {
            c.atendenteDefault = contato.usuarioPadrao
              ? ({
                  id: contato.usuarioPadrao.id,
                  name: contato.usuarioPadrao.nome,
                  descricao: contato.usuarioPadrao.apelido,
                  foto: contato.usuarioPadrao.foto,
                } as ContatoInterfaceV2['atendenteDefault'])
              : null;
          }
        }
      }
    },
    atualizarContatoName(
      state,
      action: PayloadAction<{ id: string; name: string }>,
    ) {
      const { id, name } = action.payload;
      const index = state.contatos.findIndex(c => c.id === id);
      if (index > -1) {
        state.contatos[index].nome = name;
      }
    },
    atualizarContatoStatus(
      state,
      action: PayloadAction<{ contatoId: string; status: ContatoStatusTypes }>,
    ) {
      const { contatoId, status } = action.payload;
      const indexContato = state.contatos.findIndex(c => c.id === contatoId);
      const indexContatoHistorico = state.contatosHistorico.findIndex(
        c => c.id === contatoId,
      );
      const indexContatoDialog = state.contatosDialog.findIndex(
        c => c.id === contatoId,
      );

      if (indexContato > -1) {
        state.contatos[indexContato].status = status;
      }
      if (indexContatoHistorico > -1) {
        state.contatosHistorico[indexContatoHistorico].status = status;
      }
      if (indexContatoDialog > -1) {
        state.contatosDialog[indexContatoDialog].status = status;
      }

      if (Array.isArray(state.contatosV2)) {
        const indexContatoV2 = state.contatosV2.findIndex(
          c => c.id === contatoId,
        );
        if (indexContatoV2 > -1) {
          state.contatosV2[indexContatoV2].status = status;
        }
      }
    },
    atualizarFotoContato(
      state,
      action: PayloadAction<{ contatoId: string; foto: string }>,
    ) {
      const { contatoId, foto } = action.payload;
      const indexContato = state.contatos.findIndex(c => c.id === contatoId);
      const indexContatoHistorico = state.contatosHistorico.findIndex(
        c => c.id === contatoId,
      );
      const indexContatoDialog = state.contatosDialog.findIndex(
        c => c.id === contatoId,
      );

      if (indexContato > -1) {
        state.contatos[indexContato].foto = foto;
      }
      if (indexContatoHistorico > -1) {
        state.contatosHistorico[indexContatoHistorico].foto = foto;
      }
      if (indexContatoDialog > -1) {
        state.contatosDialog[indexContatoDialog].foto = foto;
      }

      if (Array.isArray(state.contatosV2)) {
        const indexContatoV2 = state.contatosV2.findIndex(
          c => c.id === contatoId,
        );
        if (indexContatoV2 > -1) {
          state.contatosV2[indexContatoV2].fotoUrl = foto;
        }
      }
    },
    atualizarTagsContatoAtendimentoV2(
      state,
      action: PayloadAction<{ contatoId: string; tag: TagInterfaceV2 }>,
    ) {
      const { contatoId, tag } = action.payload;

      if (
        state.contatoAtendimentoV2 &&
        state.contatoAtendimentoV2.id === contatoId
      ) {
        const tags = state.contatoAtendimentoV2.tags ?? [];
        const existingIndex = tags.findIndex(t => t.id === tag.id);

        if (existingIndex > -1) {
          tags.splice(existingIndex, 1);
        } else {
          tags.push(tag);
        }

        state.contatoAtendimentoV2.tags = tags;
      }
    },
    resetContatos(state) {
      state.contatos = initialState.contatos;
      state.contatosCursor = initialState.contatosCursor;
    },
    resetContatosV2(state) {
      state.contatosV2 = initialState.contatosV2;
      state.contatosV2InfoPage = initialState.contatosV2InfoPage;
    },
    resetContatosHistorico(state) {
      state.contatosHistorico = initialState.contatosHistorico;
      state.contatosHistoricoCursor = initialState.contatosHistoricoCursor;
    },
    resetContatosDialog(state) {
      state.contatosDialog = initialState.contatosDialog;
      state.contatosDialogCursor = initialState.contatosDialogCursor;
    },
    resetContatosDialogV2(state) {
      state.contatosDialogV2 = initialState.contatosDialogV2;
      state.contatosDialogV2Cursor = initialState.contatosDialogV2Cursor;
    },
    resetContatoAtendimento(state) {
      state.contatoAtendimento = initialState.contatoAtendimento;
    },
    resetContatoAtendimentoV2(state) {
      state.contatoAtendimentoV2 = initialState.contatoAtendimentoV2;
    },
    resetMidiasContato(state) {
      state.midiasContato = initialState.midiasContato;
      state.midiasContatoCursor = initialState.midiasContatoCursor;
    },
  },
  extraReducers(builder) {
    // Fetch contatos utilizados na pagina de contatos
    builder.addCase(fetchContatos.pending, state => {
      state.loadingContatos = true;
    });
    builder.addCase(fetchContatos.fulfilled, (state, action) => {
      state.contatos = state.contatos.concat(action.payload.data);
      state.contatosCursor = action.payload.cursor;
      state.loadingContatos = false;
    });

    // Fetch contatos v2 utilizados na página de contatos (paginação por offset)
    builder.addCase(fetchContatosV2.pending, state => {
      state.loadingContatosV2 = true;
    });
    builder.addCase(fetchContatosV2.fulfilled, (state, action) => {
      if (action.payload.page > 1) {
        state.contatosV2 = [...state.contatosV2, ...action.payload.data];
      } else {
        state.contatosV2 = action.payload.data;
      }
      state.contatosV2InfoPage.page = action.payload.page;
      state.contatosV2InfoPage.total = action.payload.total;
      state.loadingContatosV2 = false;
    });
    builder.addCase(fetchContatosV2.rejected, state => {
      state.loadingContatosV2 = false;
    });
    // Fetch contatos utilizados na pagina de historico de atendimentos
    builder.addCase(fetchContatosHistorico.pending, state => {
      state.loadingContatosHistorico = true;
    });
    builder.addCase(fetchContatosHistorico.fulfilled, (state, action) => {
      state.contatosHistorico = state.contatosHistorico.concat(
        action.payload.data,
      );
      state.contatosHistoricoCursor = action.payload.cursor;
      state.loadingContatosHistorico = false;
    });
    // Fetch contatos utilizados na lista de contatos no dialog ao iniciar atendimento
    builder.addCase(fetchContatosDialog.pending, state => {
      state.loadingContatosDialog = true;
    });
    builder.addCase(fetchContatosDialog.fulfilled, (state, action) => {
      state.contatosDialog = state.contatosDialog.concat(action.payload.data);
      state.contatosDialogCursor = action.payload.cursor;
      state.loadingContatosDialog = false;
    });

    // Fetch select contatos v2 utilizados na lista de contatos no dialog ao iniciar atendimento
    builder.addCase(fetchContatosDialogV2.pending, state => {
      state.loadingContatosDialogV2 = true;
    });
    builder.addCase(fetchContatosDialogV2.fulfilled, (state, action) => {
      state.contatosDialogV2 = state.contatosDialogV2.concat(
        action.payload.data,
      );
      state.contatosDialogV2Cursor = action.payload.cursor;
      state.loadingContatosDialogV2 = false;
    });

    // Fetch midias do contato v2
    builder.addCase(fetchMidiasContato.pending, state => {
      state.loadingMidiasContato = true;
    });
    builder.addCase(fetchMidiasContato.fulfilled, (state, action) => {
      state.midiasContato = state.midiasContato.concat(action.payload.data);

      state.midiasContatoCursor = action.payload.cursor;
      state.loadingMidiasContato = false;
    });
    builder.addCase(fetchMidiasContato.rejected, state => {
      state.loadingMidiasContato = false;
    });

    // Remover contato
    builder.addCase(removerContato.pending, state => {
      state.loadingRemoverContato = true;
    });
    builder.addCase(removerContato.fulfilled, state => {
      state.loadingRemoverContato = false;
    });
    builder.addCase(removerContato.rejected, state => {
      state.loadingRemoverContato = false;
    });

    // Remover contato v2
    builder.addCase(removerContatoV2.pending, state => {
      state.loadingRemoverContato = true;
    });
    builder.addCase(removerContatoV2.fulfilled, state => {
      state.loadingRemoverContato = false;
    });
    builder.addCase(removerContatoV2.rejected, state => {
      state.loadingRemoverContato = false;
    });
    // Add tag contato
    builder.addCase(fetchAddTagContato.pending, state => {
      state.loadingAddTagContato = true;
    });
    builder.addCase(fetchAddTagContato.fulfilled, (state, action) => {
      const {
        contatos: stateContatos,
        contatosHistorico,
        contatoAtendimento,
        contatoAtendimentoV2,
      } = state;
      const { data: newTag, contatoId, isContatoAtendimento } = action.payload;

      /* Adicionando a tag ao contato */
      if (Array.isArray(stateContatos)) {
        const indexContato = stateContatos.findIndex(
          contato => contato.id === contatoId,
        );
        if (indexContato > -1) {
          stateContatos[indexContato].tags.push(newTag);
        }
      }

      /* Adicionado a tag ao contato do histórico */
      if (Array.isArray(contatosHistorico)) {
        const indexContatoHistorico = contatosHistorico.findIndex(
          contato => contato.id === contatoId,
        );
        if (indexContatoHistorico > -1) {
          contatosHistorico[indexContatoHistorico].tags.push(newTag);
        }
      }
      /* Adicionando a tag ao contato [drawer detalhes do atendimento] */
      if (isContatoAtendimento && contatoAtendimento) {
        contatoAtendimento.tags.push(newTag);
      }

      /* Adicionando a tag ao contato [contato selecionado] */
      if (contatoAtendimentoV2) {
        const tagv2 = { id: newTag.id, name: newTag.nome, color: newTag.cor };
        contatoAtendimentoV2.tags.push(tagv2);
      }

      /* Adicionando a tag ao contato V2 */
      if (Array.isArray(state.contatosV2)) {
        const indexContatoV2 = state.contatosV2.findIndex(
          contato => contato.id === contatoId,
        );
        if (indexContatoV2 > -1) {
          const tagv2 = { id: newTag.id, name: newTag.nome, color: newTag.cor };
          state.contatosV2[indexContatoV2].tags.push(tagv2);
        }
      }

      state.loadingAddTagContato = false;
    });
    builder.addCase(fetchAddTagContato.rejected, state => {
      state.loadingAddTagContato = false;
    });
    // Remover tag contato
    builder.addCase(fetchRemoveTagContato.pending, state => {
      state.loadingRemoveTagContato = true;
    });
    builder.addCase(fetchRemoveTagContato.fulfilled, (state, action) => {
      const {
        contatos: stateContatos,
        contatosHistorico,
        contatoAtendimento,
        contatoAtendimentoV2,
      } = state;
      const { tagId, contatoId, isContatoAtendimento } = action.payload;

      /* Removendo a tag do contato */
      if (Array.isArray(stateContatos)) {
        const indexContato = stateContatos.findIndex(
          contato => contato.id === contatoId,
        );
        if (indexContato > -1) {
          stateContatos[indexContato].tags = state.contatos[
            indexContato
          ].tags.filter(tag => tag.id !== tagId);
        }
      }

      /* Removendo a tag do contato [Histórico] */
      if (Array.isArray(contatosHistorico)) {
        const indexContatoHistorico = contatosHistorico.findIndex(
          contato => contato.id === contatoId,
        );

        /* Removendo a tag do contato selecionado */
        if (contatoAtendimentoV2) {
          contatoAtendimentoV2.tags = contatoAtendimentoV2.tags.filter(
            tag => tag.id !== tagId,
          );
        }

        if (indexContatoHistorico > -1) {
          contatosHistorico[indexContatoHistorico].tags = contatosHistorico[
            indexContatoHistorico
          ].tags.filter(tag => tag.id !== tagId);
        }
      }

      /* Removendo a tag do contato [drawer detalhes do atendimento] */
      if (isContatoAtendimento && contatoAtendimento) {
        contatoAtendimento.tags = contatoAtendimento.tags.filter(
          tag => tag.id !== tagId,
        );
      }

      /* Removendo a tag do contato [contato selecionado v2] */
      if (contatoAtendimentoV2) {
        contatoAtendimentoV2.tags = contatoAtendimentoV2.tags.filter(
          tag => tag.id !== tagId,
        );
      }

      /* Removendo a tag do contato V2 */
      if (Array.isArray(state.contatosV2)) {
        const indexContatoV2 = state.contatosV2.findIndex(
          contato => contato.id === contatoId,
        );
        if (indexContatoV2 > -1) {
          state.contatosV2[indexContatoV2].tags = state.contatosV2[
            indexContatoV2
          ].tags.filter(tag => tag.id !== tagId);
        }
      }

      state.loadingRemoveTagContato = false;
    });
    builder.addCase(fetchRemoveTagContato.rejected, state => {
      state.loadingRemoveTagContato = false;
    });

    // fetchContatoAtendimento
    builder.addCase(fetchContatoAtendimento.pending, state => {
      state.loadingContatoAtendimento = true;
    });
    builder.addCase(fetchContatoAtendimento.fulfilled, (state, action) => {
      state.loadingContatoAtendimento = false;
      state.contatoAtendimento = action.payload;
    });
    builder.addCase(fetchContatoAtendimento.rejected, state => {
      state.loadingContatoAtendimento = false;
    });

    // fetchContatoAtendimentoV2
    builder.addCase(fetchContatoAtendimentoV2.pending, state => {
      state.loadingContatoAtendimentoV2 = true;
    });
    builder.addCase(fetchContatoAtendimentoV2.fulfilled, (state, action) => {
      state.loadingContatoAtendimentoV2 = false;
      state.contatoAtendimentoV2 = action.payload;
    });
    builder.addCase(fetchContatoAtendimentoV2.rejected, state => {
      state.loadingContatoAtendimentoV2 = false;
    });

    // fetchCustomFields
    builder.addCase(fetchCustomFields.pending, state => {
      state.loadingCustomFields = true;
    });
    builder.addCase(fetchCustomFields.fulfilled, (state, action) => {
      state.loadingCustomFields = false;
      state.customFields = action.payload;
    });
    builder.addCase(fetchCustomFields.rejected, state => {
      state.loadingCustomFields = false;
    });

    // definir-equipe-ou-atendente-padrao
    builder.addCase(definirEquipeOuAtendentePadrao.pending, state => {
      state.loadingdefinirEquipeOuAtendentePadrao = true;
    });
    builder.addCase(definirEquipeOuAtendentePadrao.fulfilled, state => {
      state.loadingdefinirEquipeOuAtendentePadrao = false;
    });
    builder.addCase(definirEquipeOuAtendentePadrao.rejected, state => {
      state.loadingdefinirEquipeOuAtendentePadrao = false;
    });

    // funnel and opportunities
    builder.addCase(fetchFunnelsAndOpportunities.pending, state => {
      state.loadingFunnelsAndOpportunities = true;
    });
    builder.addCase(fetchFunnelsAndOpportunities.fulfilled, (state, action) => {
      state.loadingFunnelsAndOpportunities = false;
      state.funnelsAndOpportunities = action.payload;
    });
    builder.addCase(fetchFunnelsAndOpportunities.rejected, state => {
      state.loadingFunnelsAndOpportunities = false;
    });
  },
});

// actions

export const {
  atualizarContatoStatus,
  atualizarContatoV2,
  updateListaContatos,
  atualizarContatoName,
  atualizarFotoContato,
  atualizarTagsContatoAtendimentoV2,
  resetContatos,
  resetContatosV2,
  resetContatosHistorico,
  resetContatosDialog,
  resetContatosDialogV2,
  resetContatoAtendimento,
  resetContatoAtendimentoV2,
  resetMidiasContato,
} = contatosSlice.actions;

// selectors

export const contatosSelector = (state: RootState) => state.contatos;

export default contatosSlice.reducer;
